import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import {
  applyProposedDraft,
  getTicket,
  reanalyzeTicket,
  saveDraft,
  sendChat,
  sendReply,
  setStatus,
  syncDrafts,
  Ticket,
} from '../lib/api';
import { formatMailDate } from '../lib/dates';
import { EmailBody } from './EmailBody';
import { ProgressBanner } from './ProgressBanner';

type Props = {
  ticketId: string;
  onUpdated?: () => void;
  onBack?: () => void;
};

type MobileTab = 'mail' | 'dossier' | 'chat' | 'draft';
type BusyKind = null | 'load' | 'reanalyze' | 'chat' | 'draft' | 'sync' | 'send' | 'status';

function asList(v: unknown): string[] {
  return Array.isArray(v) ? v.map(String) : [];
}

/** Destinatario effettivo della risposta (come usa il backend all'invio). */
function replyRecipient(ticket: Ticket | null | undefined): string {
  if (!ticket) return '';
  const direct = (ticket.sender_email || '').trim();
  if (direct) return direct;
  const from = ticket.mail?.from || '';
  const m = from.match(/[\w.+-]+@[\w.-]+\.\w+/);
  return m ? m[0] : from.trim();
}

function loadPct(key: string, fallback: number) {
  try {
    const v = Number(localStorage.getItem(key));
    if (Number.isFinite(v) && v >= 25 && v <= 75) return v;
  } catch {
    /* ignore */
  }
  return fallback;
}

function savePct(key: string, v: number) {
  try {
    localStorage.setItem(key, String(Math.round(v)));
  } catch {
    /* ignore */
  }
}

const BUSY_LABELS: Record<Exclude<BusyKind, null>, string> = {
  load: 'Caricamento ticket…',
  reanalyze: 'Rielaborazione in corso…',
  chat: 'L’agent sta elaborando la risposta…',
  draft: 'Salvataggio bozza…',
  sync: 'Salvataggio bozza su IMAP…',
  send: 'Invio email in corso…',
  status: 'Aggiornamento stato…',
};

export function TicketWorkspace({ ticketId, onUpdated, onBack }: Props) {
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [draft, setDraft] = useState('');
  const [toEmail, setToEmail] = useState('');
  const [chatInput, setChatInput] = useState('');
  const [busyKind, setBusyKind] = useState<BusyKind>('load');
  const [waitSecs, setWaitSecs] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [colPct, setColPct] = useState(() => loadPct('supporto_ws_col', 52));
  const [rowPct, setRowPct] = useState(() => loadPct('supporto_ws_row', 48));
  const [mobileTab, setMobileTab] = useState<MobileTab>('mail');
  const workspaceRef = useRef<HTMLDivElement>(null);
  const pollAbort = useRef(0);
  /** Evita che il poll sovrascriva modifiche locali non ancora salvate. */
  const draftLocalEdit = useRef(false);
  const toLocalEdit = useRef(false);

  const applyTicket = useCallback((t: Ticket, opts?: { forceDraft?: boolean }) => {
    setTicket(t);
    if (opts?.forceDraft) {
      draftLocalEdit.current = false;
      toLocalEdit.current = false;
    }
    // draft_dirty sul server = "non far sovrascrivere dall'agent", NON "nascondi la bozza"
    if (!draftLocalEdit.current) {
      setDraft(t.draft_customer || '');
    }
    if (!toLocalEdit.current) {
      setToEmail(replyRecipient(t));
    }
  }, []);

  const pollUntilReady = useCallback(
    async (startedAt: number, timeoutMs = 120_000) => {
      const token = ++pollAbort.current;
      const deadline = Date.now() + timeoutMs;
      while (Date.now() < deadline) {
        if (token !== pollAbort.current) return null;
        await new Promise((r) => setTimeout(r, 2000));
        if (token !== pollAbort.current) return null;
        setWaitSecs(Math.round((Date.now() - startedAt) / 1000));
        const res = await getTicket(ticketId);
        applyTicket(res.ticket);
        if (res.ticket.status !== 'in_analisi') {
          onUpdated?.();
          return res.ticket;
        }
      }
      return null;
    },
    [applyTicket, onUpdated, ticketId],
  );

  useEffect(() => {
    setError(null);
    setNotice(null);
    setMobileTab('mail');
    setBusyKind('load');
    setWaitSecs(0);
    setDraft('');
    setToEmail('');
    draftLocalEdit.current = false;
    toLocalEdit.current = false;
    pollAbort.current += 1;
    let cancelled = false;
    (async () => {
      try {
        const res = await getTicket(ticketId);
        if (cancelled) return;
        applyTicket(res.ticket);
        if (res.ticket.status === 'in_analisi') {
          setBusyKind('reanalyze');
          const started = Date.now();
          const done = await pollUntilReady(started);
          if (cancelled) return;
          if (!done) {
            setError('Analisi ancora in corso sul server. Attendi o riprova tra un minuto.');
          }
          setBusyKind(null);
        } else {
          setBusyKind(null);
        }
      } catch (e) {
        if (!cancelled) {
          setError(String((e as Error).message || e));
          setBusyKind(null);
        }
      }
    })();
    return () => {
      cancelled = true;
      pollAbort.current += 1;
    };
  }, [ticketId, applyTicket, pollUntilReady]);

  // Contatore secondi mentre busy
  useEffect(() => {
    if (!busyKind) {
      setWaitSecs(0);
      return;
    }
    const t0 = Date.now();
    const id = window.setInterval(() => setWaitSecs(Math.round((Date.now() - t0) / 1000)), 1000);
    return () => window.clearInterval(id);
  }, [busyKind]);

  function startColDrag(e: React.MouseEvent) {
    e.preventDefault();
    const box = workspaceRef.current?.getBoundingClientRect();
    if (!box) return;
    const onMove = (ev: MouseEvent) => {
      const pct = ((ev.clientX - box.left) / box.width) * 100;
      const next = Math.min(75, Math.max(25, pct));
      setColPct(next);
      savePct('supporto_ws_col', next);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }

  function startRowDrag(e: React.MouseEvent) {
    e.preventDefault();
    const box = workspaceRef.current?.getBoundingClientRect();
    if (!box) return;
    const onMove = (ev: MouseEvent) => {
      const pct = ((ev.clientY - box.top) / box.height) * 100;
      const next = Math.min(75, Math.max(25, pct));
      setRowPct(next);
      savePct('supporto_ws_row', next);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }

  async function onChat(e: FormEvent) {
    e.preventDefault();
    if (!chatInput.trim() || busyKind) return;
    setBusyKind('chat');
    setError(null);
    setNotice(null);
    setWaitSecs(0);
    const started = Date.now();
    const text = chatInput.trim();
    try {
      const res = await sendChat(ticketId, text);
      setChatInput('');
      if (res.ticket) applyTicket(res.ticket);
      if (res.async) {
        const done = await pollUntilReady(started, 180_000);
        if (!done) {
          setError('Risposta ancora in corso sul server. Ricarica tra qualche secondo.');
        } else {
          setNotice(
            'Nuova proposta agent pronta. Clicca «Applica proposta agent» per aggiornare la bozza (e il destinatario se corretto).',
          );
          setMobileTab('draft');
        }
      } else if (res.ticket?.draft_proposed) {
        setNotice(
          'Nuova proposta agent pronta. Clicca «Applica proposta agent» per aggiornare la bozza.',
        );
        setMobileTab('draft');
      }
      onUpdated?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyKind(null);
    }
  }

  async function onSaveDraft() {
    if (busyKind) return;
    setBusyKind('draft');
    try {
      const res = await saveDraft(ticketId, draft, toEmail);
      applyTicket(res.ticket, { forceDraft: true });
      setNotice('Bozza salvata.');
      onUpdated?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyKind(null);
    }
  }

  async function onApplyProposed() {
    if (busyKind) return;
    setBusyKind('draft');
    setError(null);
    try {
      const res = await applyProposedDraft(ticketId);
      applyTicket(res.ticket, { forceDraft: true });
      setNotice('Proposta agent applicata alla bozza.');
      setMobileTab('draft');
      onUpdated?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyKind(null);
    }
  }

  async function onSyncImap() {
    if (busyKind) return;
    if (!draft.trim()) {
      setError('Bozza vuota: scrivi o genera una risposta prima di salvarla su IMAP.');
      return;
    }
    const to = toEmail.trim() || replyRecipient(ticket) || 'destinatario';
    if (
      !confirm(
        `Salvare la bozza cliente nella cartella Bozze della casella supporto@ (IMAP)?\nDestinatario: ${to}\nNon viene inviata al cliente.`,
      )
    ) {
      return;
    }
    setBusyKind('sync');
    setError(null);
    setNotice(null);
    try {
      await saveDraft(ticketId, draft, to);
      const res = await syncDrafts(ticketId, draft);
      applyTicket(res.ticket, { forceDraft: true });
      setNotice(res.message || `Bozza salvata su IMAP${res.folder ? ` (${res.folder})` : ''}`);
      onUpdated?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyKind(null);
    }
  }

  async function onSendEmail() {
    if (busyKind) return;
    if (!draft.trim()) {
      setError('Bozza vuota: scrivi o genera una risposta prima di inviare.');
      return;
    }
    const to = toEmail.trim() || replyRecipient(ticket) || 'destinatario sconosciuto';
    if (
      !confirm(
        `Inviare ora la risposta a:\n${to}\n\nda supporto@qualityandsafety.org?\nL'azione non è annullabile.`,
      )
    ) {
      return;
    }
    setBusyKind('send');
    setError(null);
    setNotice(null);
    try {
      // Salva destinatario + bozza prima dell'invio
      await saveDraft(ticketId, draft, to);
      const res = await sendReply(ticketId, draft);
      applyTicket(res.ticket, { forceDraft: true });
      setNotice(res.message || `Email inviata a ${res.to || to}`);
      onUpdated?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyKind(null);
    }
  }

  async function onReanalyze() {
    if (busyKind) return;
    if (!confirm('Rilanciare l’analisi automatica con il modello attuale?')) return;
    setBusyKind('reanalyze');
    setError(null);
    const started = Date.now();
    try {
      const start = await reanalyzeTicket(ticketId);
      if (start.ticket) applyTicket(start.ticket);
      const done = await pollUntilReady(started, 120_000);
      if (!done) {
        setError('Analisi ancora in corso sul server. Puoi ricaricare tra qualche secondo.');
      }
      onUpdated?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyKind(null);
    }
  }

  async function mark(status: string) {
    if (busyKind) return;
    setBusyKind('status');
    try {
      const res = await setStatus(ticketId, status);
      applyTicket(res.ticket);
      onUpdated?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyKind(null);
    }
  }

  const analyzing =
    busyKind === 'reanalyze' || busyKind === 'chat' || ticket?.status === 'in_analisi';
  const locked = Boolean(busyKind) || ticket?.status === 'in_analisi';

  if (!ticket && busyKind === 'load') {
    return (
      <div className="empty">
        <ProgressBanner label="Caricamento ticket…" />
      </div>
    );
  }

  if (!ticket) {
    return <div className="empty">{error || 'Ticket non trovato'}</div>;
  }

  const dossier = ticket.dossier || {};
  const rischio = String(dossier.rischio || '');
  const recipient = toEmail.trim() || replyRecipient(ticket);
  const proposed = (ticket.draft_proposed || '').trim();
  const hasPendingProposal =
    Boolean(proposed) &&
    (proposed !== draft.trim() ||
      proposed !== (ticket.draft_customer || '').trim() ||
      Boolean(
        ticket.proposed_sender_email &&
          ticket.proposed_sender_email.toLowerCase() !== recipient.toLowerCase(),
      ));
  const rightPct = 100 - colPct;
  const bottomPct = 100 - rowPct;
  const progressLabel = busyKind
    ? BUSY_LABELS[busyKind]
    : analyzing
      ? BUSY_LABELS.reanalyze
      : null;
  const sendLabel = busyKind === 'send'
    ? 'Invio…'
    : recipient
      ? `Invia a ${recipient}`
      : 'Invia email';

  return (
    <>
      <div className="topbar">
        <div className="topbar-title">
          {onBack && (
            <button type="button" className="btn back-btn" onClick={onBack} aria-label="Torna alla lista">
              ← Lista
            </button>
          )}
          <div className="topbar-text">
            <strong>{ticket.mail?.subject || '(senza oggetto)'}</strong>
            <div className="topbar-meta">
              {ticket.product || 'n/d'} ·{' '}
              <span className={analyzing ? 'status-analyzing' : undefined}>{ticket.status}</span>
              {rischio && (
                <>
                  {' '}
                  · <span className={`badge ${rischio}`}>{rischio}</span>
                </>
              )}
              {recipient && (
                <>
                  {' · '}
                  A: <strong className="recipient-inline">{recipient}</strong>
                </>
              )}
              {' · '}
              Arrivo: {formatMailDate(ticket.mail?.date || ticket.updated_at)}
              {ticket.draft_dirty ? ' · bozza modificata a mano' : ''}
            </div>
          </div>
        </div>
        <div className="actions">
          <button className="btn" disabled={locked} onClick={() => void onReanalyze()}>
            {analyzing ? 'In analisi…' : 'Rielabora'}
          </button>
          <button className="btn" disabled={locked} onClick={() => void mark('pronto')}>
            Pronto
          </button>
          <button className="btn" disabled={locked} onClick={() => void mark('chiuso')}>
            Chiudi
          </button>
          <button className="btn" disabled={locked} onClick={() => void onSyncImap()}>
            {busyKind === 'sync' ? 'Salvataggio IMAP…' : 'Salva come bozza su IMAP'}
          </button>
          <button
            className="btn primary"
            disabled={locked}
            onClick={() => void onSendEmail()}
            title={recipient ? `Invia risposta a ${recipient}` : 'Invia email'}
          >
            {sendLabel}
          </button>
        </div>
      </div>

      {progressLabel && (
        <ProgressBanner
          label={progressLabel}
          detail={
            waitSecs > 0
              ? `${waitSecs}s — Opus può impiegare fino a 1–2 minuti con i tool Cognito/Stripe`
              : 'Attendere, non chiudere la pagina'
          }
        />
      )}

      {error && (
        <div className="error" style={{ padding: '0.5rem 1rem' }}>
          {error}
        </div>
      )}
      {notice && !error && (
        <div className="notice" style={{ padding: '0.5rem 1rem' }}>
          {notice}
        </div>
      )}

      <div className="mobile-tabs" role="tablist" aria-label="Sezioni ticket">
        {(
          [
            ['mail', 'Email'],
            ['dossier', 'Dossier'],
            ['chat', 'Chat'],
            ['draft', 'Bozza'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={mobileTab === id}
            className={`mobile-tab${mobileTab === id ? ' active' : ''}`}
            onClick={() => setMobileTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      <div
        ref={workspaceRef}
        className={`workspace mobile-tab-${mobileTab}`}
        style={{
          gridTemplateColumns: `minmax(0, ${colPct}fr) 6px minmax(0, ${rightPct}fr)`,
          gridTemplateRows: `minmax(0, ${rowPct}fr) 6px minmax(0, ${bottomPct}fr)`,
        }}
      >
        <section className="panel panel-mail" data-tab="mail">
          <h2>Email originale</h2>
          <div className="panel-body panel-body-mail">
            <EmailBody
              from={ticket.mail?.from}
              dateLabel={formatMailDate(ticket.mail?.date)}
              bodyText={ticket.mail?.body_text}
              bodyHtml={ticket.mail?.body_html}
            />
          </div>
        </section>

        <div
          className="splitter splitter-vertical"
          onMouseDown={startColDrag}
          role="separator"
          aria-orientation="vertical"
        />

        <section className="panel panel-dossier" data-tab="dossier">
          <h2>Dossier interno</h2>
          <div className="panel-body">
            <strong>Riepilogo</strong>
            {'\n'}
            {String(dossier.riepilogo_richiesta || '—')}
            {'\n\n'}
            <strong>Ragionamento</strong>
            {'\n'}
            {String(dossier.ragionamento || '—')}
            {'\n\n'}
            <strong>Cliente</strong>
            {'\n'}
            {JSON.stringify(dossier.cliente || {}, null, 2)}
            {'\n\n'}
            <strong>Evidenze</strong>
            {'\n'}
            {asList(dossier.evidenze)
              .map((e) => `• ${e}`)
              .join('\n') || '—'}
            {'\n\n'}
            <strong>Non verificato</strong>
            {'\n'}
            {asList(dossier.non_verificato)
              .map((e) => `• ${e}`)
              .join('\n') || '—'}
            {'\n\n'}
            <strong>Checklist</strong>
            {'\n'}
            {asList(dossier.checklist)
              .map((e) => `• ${e}`)
              .join('\n') || '—'}
          </div>
        </section>

        <div
          className="splitter splitter-horizontal"
          onMouseDown={startRowDrag}
          role="separator"
          aria-orientation="horizontal"
        />
        <div className="splitter-cross" aria-hidden />
        <div
          className="splitter splitter-horizontal"
          onMouseDown={startRowDrag}
          role="separator"
          aria-orientation="horizontal"
        />

        <section className="panel panel-chat" data-tab="chat">
          <h2>Chat rielaborazione</h2>
          <div className="panel-body chat-log">
            {(ticket.chat || []).map((m, i) => (
              <div key={i} className={`bubble ${m.role}`}>
                <div className="role">{m.role}</div>
                {m.content}
              </div>
            ))}
            {analyzing && (
              <div className="bubble system bubble-progress">
                <span className="spinner spinner-sm" aria-hidden />
                Analisi in corso… ({waitSecs}s)
              </div>
            )}
          </div>
          <form className="chat-compose" onSubmit={onChat}>
            <input
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              placeholder="Es. controlla Stripe, tono più formale…"
              disabled={locked}
            />
            <button className="btn primary" type="submit" disabled={locked || !chatInput.trim()}>
              {busyKind === 'chat' ? 'Invio…' : 'Invia'}
            </button>
          </form>
        </section>

        <div
          className="splitter splitter-vertical"
          onMouseDown={startColDrag}
          role="separator"
          aria-orientation="vertical"
        />

        <section className="panel panel-draft" data-tab="draft">
          <h2>Bozza cliente</h2>
          {hasPendingProposal && (
            <div className="draft-apply-banner">
              <div>
                <strong>Proposta agent pronta</strong>
                {ticket.proposed_sender_email && (
                  <span>
                    {' '}
                    · destinatario proposto: {ticket.proposed_sender_email}
                  </span>
                )}
                <div className="draft-apply-hint">
                  Clicca per copiare il testo (e il destinatario) nella bozza da inviare.
                </div>
              </div>
              <button
                className="btn primary"
                disabled={locked}
                onClick={() => void onApplyProposed()}
              >
                {busyKind === 'draft' ? 'Applicazione…' : 'Applica proposta agent'}
              </button>
            </div>
          )}
          <div className="draft-meta" aria-label="Destinatario risposta">
            <label className="draft-to-row">
              <span className="draft-meta-label">A</span>
              <input
                className="draft-to-input"
                type="email"
                value={toEmail}
                onChange={(e) => {
                  toLocalEdit.current = true;
                  setToEmail(e.target.value);
                }}
                placeholder="destinatario@…"
                disabled={locked}
                aria-label="Destinatario email"
              />
            </label>
            <div>
              <span className="draft-meta-label">Da</span>
              supporto@qualityandsafety.org
            </div>
            {ticket.mail?.from && (
              <div className="draft-meta-from">
                Mittente IMAP: {ticket.mail.from}
              </div>
            )}
          </div>
          <textarea
            className="draft-area"
            value={draft}
            onChange={(e) => {
              draftLocalEdit.current = true;
              setDraft(e.target.value);
            }}
            placeholder="Testo che andrà al cliente…"
            disabled={locked}
          />
          <div className="draft-actions">
            <button className="btn" disabled={locked} onClick={() => void onSaveDraft()}>
              {busyKind === 'draft' ? 'Salvataggio…' : 'Salva bozza'}
            </button>
            {hasPendingProposal && (
              <button className="btn accent" disabled={locked} onClick={() => void onApplyProposed()}>
                Applica proposta agent
              </button>
            )}
            <button className="btn" disabled={locked} onClick={() => void onSyncImap()}>
              {busyKind === 'sync' ? 'Salvataggio IMAP…' : 'Salva come bozza su IMAP'}
            </button>
            <button
              className="btn primary"
              disabled={locked}
              onClick={() => void onSendEmail()}
              title={recipient ? `Invia risposta a ${recipient}` : 'Invia email'}
            >
              {sendLabel}
            </button>
          </div>
        </section>
      </div>
    </>
  );
}
